import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Bot,
  GitBranch,
  Hand,
  HelpCircle,
  ListChecks,
  MessageSquare,
  Plus,
  Square,
  Trash2,
  Zap,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import {
  useDeleteSalesbot,
  useSalesbotStatus,
  useSalesbots,
  useSaveSalesbot,
  type BotStep,
  type Salesbot,
} from './api';

const STEP_TYPES: { type: BotStep['type']; label: string; icon: typeof Bot; hint: string }[] = [
  { type: 'message', label: 'Send a message', icon: MessageSquare, hint: 'Says something and carries on.' },
  { type: 'question', label: 'Ask a question', icon: HelpCircle, hint: 'Waits for the answer and keeps it.' },
  { type: 'buttons', label: 'Offer buttons', icon: ListChecks, hint: 'Each button can lead somewhere else.' },
  { type: 'condition', label: 'Check an answer', icon: GitBranch, hint: 'Goes one way or the other.' },
  { type: 'action', label: 'Do something', icon: Zap, hint: 'Make the lead, tag it, assign it, tell someone.' },
  { type: 'handoff', label: 'Hand to a person', icon: Hand, hint: 'The bot stops and the team is told.' },
  { type: 'end', label: 'End', icon: Square, hint: 'The bot is finished.' },
];

const FIELDS = [
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['company', 'Company'],
  ['requirement', 'What they need'],
  ['budget', 'Budget'],
  ['city', 'City / Emirate'],
] as const;

let counter = 0;
const newId = () => `s${Date.now().toString(36).slice(-4)}${(counter += 1)}`;

function blank(type: BotStep['type']): BotStep {
  const id = newId();
  switch (type) {
    case 'message':
      return { id, type, text: '' };
    case 'question':
      return { id, type, text: '', field: 'name' };
    case 'buttons':
      return { id, type, text: '', field: null, options: [{ label: 'Yes' }, { label: 'No' }] };
    case 'condition':
      return { id, type, field: 'email', operator: 'is_empty', value: '', then: null, else: null };
    case 'action':
      return { id, type, action: 'create_lead', config: {} };
    case 'handoff':
      return { id, type, text: 'Let me connect you with a colleague.' };
    default:
      return { id, type: 'end', text: '' };
  }
}

/** A sensible first bot for a UAE business: greet, qualify, hand over. */
function starter(): BotStep[] {
  return [
    { id: 'hello', type: 'message', text: 'Hello and welcome! 👋 I can help you right away.' },
    { id: 'name', type: 'question', text: 'May I have your name?', field: 'name' },
    {
      id: 'topic',
      type: 'buttons',
      text: 'Thanks {{name}}. What are you interested in?',
      field: 'requirement',
      options: [{ label: 'Pricing' }, { label: 'Book a demo' }, { label: 'Something else', next: 'human' }],
    },
    { id: 'phone', type: 'question', text: 'What is the best number to reach you on? (with country code, e.g. +971…)', field: 'phone' },
    { id: 'lead', type: 'action', action: 'create_lead', config: {} },
    { id: 'human', type: 'handoff', text: 'Perfect — a member of our team will reply here shortly.' },
  ];
}

export function SalesbotsPage() {
  const { data: bots = [], isLoading } = useSalesbots();
  const status = useSalesbotStatus();
  const [editing, setEditing] = useState<Salesbot | 'new' | null>(null);

  return (
    <div>
      <PageHeader
        title="Salesbots"
        description="Bots that answer first — on the website chat, WhatsApp, Telegram, Messenger and Instagram — ask what you need to know, and hand over to a person."
        breadcrumbs={[{ label: 'Inbox', to: '/inbox' }, { label: 'Salesbots' }]}
        actions={
          <Can permission="inbox.salesbot.create">
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" />
              New Salesbot
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <PageLoader />
      ) : bots.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <Bot className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No Salesbots yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Start from the lead qualifier: it greets, asks the name and need, takes a phone number, makes the lead and
            hands over.
          </p>
          <Can permission="inbox.salesbot.create">
            <Button className="mt-4" onClick={() => setEditing('new')}>
              Create a Salesbot
            </Button>
          </Can>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {bots.map((bot) => (
            <div key={bot.id} className="flex flex-col rounded-xl border bg-card p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
                  <Bot className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{bot.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {bot.trigger === 'keyword'
                      ? `Starts on: ${bot.keywords.join(', ')}`
                      : bot.trigger === 'manual'
                        ? 'Started by hand or by an automation'
                        : 'Greets new conversations'}
                  </p>
                </div>
                <Can permission="inbox.salesbot.update">
                  <Switch
                    checked={bot.status === 'ACTIVE'}
                    onCheckedChange={(checked) => status.mutate({ id: bot.id, status: checked ? 'ACTIVE' : 'PAUSED' })}
                  />
                </Can>
              </div>
              {bot.description && <p className="mt-2 text-sm text-muted-foreground">{bot.description}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                <Badge variant={bot.status === 'ACTIVE' ? 'success' : 'secondary'}>{bot.status === 'ACTIVE' ? 'On' : 'Off'}</Badge>
                <Badge variant="outline">{bot.steps.length} steps</Badge>
                <Badge variant="outline">Ran {bot.runCount}×</Badge>
                {(bot._count?.channels ?? 0) + (bot._count?.whatsappAccounts ?? 0) > 0 && (
                  <Badge variant="outline">
                    On {(bot._count?.channels ?? 0) + (bot._count?.whatsappAccounts ?? 0)} channel(s)
                  </Badge>
                )}
              </div>
              <div className="mt-auto flex items-center gap-2 pt-3 text-xs text-muted-foreground">
                Edited {timeAgo(bot.updatedAt)}
                <Can permission="inbox.salesbot.update">
                  <Button size="sm" variant="outline" className="ms-auto" onClick={() => setEditing(bot)}>
                    Edit
                  </Button>
                </Can>
              </div>
            </div>
          ))}
        </div>
      )}

      {status.error && <FormError message={(status.error as ApiRequestError).message} />}
      {editing && <BotEditor bot={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function BotEditor({ bot, onClose }: { bot: Salesbot | null; onClose: () => void }) {
  const save = useSaveSalesbot();
  const remove = useDeleteSalesbot();
  const [name, setName] = useState(bot?.name ?? 'Lead qualifier');
  const [description, setDescription] = useState(bot?.description ?? '');
  const [trigger, setTrigger] = useState<Salesbot['trigger']>(bot?.trigger ?? 'new_conversation');
  const [keywords, setKeywords] = useState((bot?.keywords ?? []).join(', '));
  const [steps, setSteps] = useState<BotStep[]>(bot?.steps ?? starter());
  const [activate, setActivate] = useState(bot ? bot.status === 'ACTIVE' : true);

  const update = (index: number, step: BotStep) => setSteps(steps.map((item, position) => (position === index ? step : item)));
  const move = (index: number, by: number) => {
    const next = [...steps];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item);
    setSteps(next);
  };

  const submit = () =>
    save.mutate(
      {
        id: bot?.id,
        name,
        description: description || undefined,
        trigger,
        keywords: keywords.split(',').map((item) => item.trim()).filter(Boolean),
        steps,
        status: activate ? 'ACTIVE' : 'PAUSED',
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{bot ? `Edit ${bot.name}` : 'New Salesbot'}</DialogTitle>
          <DialogDescription>
            Steps run top to bottom. A button or a check can jump to another step. Use {'{{name}}'} to repeat an answer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Starts</Label>
            <Select value={trigger} onChange={(event) => setTrigger(event.target.value as Salesbot['trigger'])} className="h-9">
              <option value="new_conversation">On a new conversation (on the channels it is set on)</option>
              <option value="keyword">When a message contains a keyword</option>
              <option value="manual">Only by hand or from an automation</option>
            </Select>
          </div>
          {trigger === 'keyword' && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Keywords</Label>
              <Input value={keywords} onChange={(event) => setKeywords(event.target.value)} placeholder="price, demo, السعر" />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Description</Label>
            <Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="What this bot is for" />
          </div>
        </div>

        <div className="space-y-2">
          {steps.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              steps={steps}
              onChange={(next) => update(index, next)}
              onRemove={() => setSteps(steps.filter((_item, position) => position !== index))}
              onUp={index > 0 ? () => move(index, -1) : undefined}
              onDown={index < steps.length - 1 ? () => move(index, 1) : undefined}
            />
          ))}
          <div className="flex flex-wrap gap-1.5 rounded-xl border border-dashed p-2">
            {STEP_TYPES.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                onClick={() => setSteps([...steps, blank(type)])}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-3 w-3" />
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <Switch checked={activate} onCheckedChange={setActivate} /> Switch it on
        </label>

        {(save.error || remove.error) && <FormError message={((save.error ?? remove.error) as ApiRequestError).message} />}
        <DialogFooter className="gap-2">
          {bot && (
            <Can permission="inbox.salesbot.delete">
              <Button variant="ghost" className="me-auto text-destructive" onClick={() => remove.mutate(bot.id, { onSuccess: onClose })}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </Can>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || name.trim().length < 2 || steps.length === 0}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepCard({
  step,
  index,
  steps,
  onChange,
  onRemove,
  onUp,
  onDown,
}: {
  step: BotStep;
  index: number;
  steps: BotStep[];
  onChange: (step: BotStep) => void;
  onRemove: () => void;
  onUp?: () => void;
  onDown?: () => void;
}) {
  const meta = STEP_TYPES.find((item) => item.type === step.type)!;
  const Icon = meta.icon;
  const { data: users } = useUserOptions();

  const targets = steps.filter((item) => item.id !== step.id);
  const label = (item: BotStep) => `${steps.indexOf(item) + 1}. ${STEP_TYPES.find((t) => t.type === item.type)?.label ?? item.type}`;
  const TargetSelect = ({ value, onPick, empty }: { value?: string | null; onPick: (id: string | null) => void; empty: string }) => (
    <Select value={value ?? ''} onChange={(event) => onPick(event.target.value || null)} className="h-8 text-xs">
      <option value="">{empty}</option>
      {targets.map((item) => (
        <option key={item.id} value={item.id}>
          → {label(item)}
        </option>
      ))}
    </Select>
  );

  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-xs font-bold text-primary">{index + 1}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{meta.label}</span>
        <div className="ms-auto flex items-center gap-1 text-muted-foreground">
          {onUp && (
            <button onClick={onUp} className="hover:text-foreground" title="Move up">
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
          )}
          {onDown && (
            <button onClick={onDown} className="hover:text-foreground" title="Move down">
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          )}
          <button onClick={onRemove} className="hover:text-destructive" title="Remove">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {'text' in step && step.type !== 'handoff' && step.type !== 'end' && (
          <Textarea
            value={step.text ?? ''}
            onChange={(event) => onChange({ ...step, text: event.target.value } as BotStep)}
            rows={2}
            placeholder={step.type === 'question' ? 'What is your email?' : 'Message'}
            className="text-sm"
          />
        )}
        {(step.type === 'handoff' || step.type === 'end') && (
          <Input
            value={step.text ?? ''}
            onChange={(event) => onChange({ ...step, text: event.target.value })}
            placeholder="Last message (optional)"
            className="h-8 text-sm"
          />
        )}

        {step.type === 'question' && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            Keep the answer as
            <Select value={step.field} onChange={(event) => onChange({ ...step, field: event.target.value })} className="h-8 w-auto text-xs">
              {FIELDS.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Select>
          </div>
        )}

        {step.type === 'buttons' && (
          <div className="space-y-1.5">
            {step.options.map((option, position) => (
              <div key={position} className="flex items-center gap-2">
                <Input
                  value={option.label}
                  maxLength={40}
                  onChange={(event) =>
                    onChange({
                      ...step,
                      options: step.options.map((item, at) => (at === position ? { ...item, label: event.target.value } : item)),
                    })
                  }
                  className="h-8 flex-1 text-xs"
                />
                <div className="w-48">
                  <TargetSelect
                    value={option.next}
                    empty="→ next step"
                    onPick={(next) =>
                      onChange({ ...step, options: step.options.map((item, at) => (at === position ? { ...item, next } : item)) })
                    }
                  />
                </div>
                <button
                  onClick={() => onChange({ ...step, options: step.options.filter((_item, at) => at !== position) })}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {step.options.length < 10 && (
                <button onClick={() => onChange({ ...step, options: [...step.options, { label: '' }] })} className="text-link hover:underline">
                  + Button
                </button>
              )}
              <span className="ms-auto text-muted-foreground">Keep the choice as</span>
              <Select
                value={step.field ?? ''}
                onChange={(event) => onChange({ ...step, field: event.target.value || null })}
                className="h-8 w-auto text-xs"
              >
                <option value="">Don’t keep</option>
                {FIELDS.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}

        {step.type === 'condition' && (
          <div className="grid gap-2 sm:grid-cols-3">
            <Select value={step.field} onChange={(event) => onChange({ ...step, field: event.target.value })} className="h-8 text-xs">
              {FIELDS.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </Select>
            <Select
              value={step.operator}
              onChange={(event) => onChange({ ...step, operator: event.target.value as typeof step.operator })}
              className="h-8 text-xs"
            >
              <option value="is_empty">is not known</option>
              <option value="is_not_empty">is known</option>
              <option value="contains">contains</option>
              <option value="equals">is exactly</option>
            </Select>
            {(step.operator === 'contains' || step.operator === 'equals') && (
              <Input value={step.value ?? ''} onChange={(event) => onChange({ ...step, value: event.target.value })} className="h-8 text-xs" />
            )}
            <div className="sm:col-span-3 grid gap-2 sm:grid-cols-2">
              <div className="text-xs">
                If yes <TargetSelect value={step.then} empty="→ next step" onPick={(then) => onChange({ ...step, then })} />
              </div>
              <div className="text-xs">
                If no <TargetSelect value={step.else} empty="→ next step" onPick={(other) => onChange({ ...step, else: other })} />
              </div>
            </div>
          </div>
        )}

        {step.type === 'action' && (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={step.action}
              onChange={(event) => onChange({ ...step, action: event.target.value as typeof step.action, config: {} })}
              className="h-8 w-auto text-xs"
            >
              <option value="create_lead">Make / update the lead with the answers</option>
              <option value="add_tag">Tag the lead</option>
              <option value="assign">Assign the conversation</option>
              <option value="notify">Notify someone</option>
            </Select>
            {step.action === 'add_tag' && (
              <Input
                value={step.config?.tag ?? ''}
                onChange={(event) => onChange({ ...step, config: { tag: event.target.value } })}
                placeholder="hot-lead"
                className="h-8 w-40 text-xs"
              />
            )}
            {(step.action === 'assign' || step.action === 'notify') && (
              <Select
                value={step.config?.userId ?? ''}
                onChange={(event) => onChange({ ...step, config: { ...step.config, userId: event.target.value } })}
                className="h-8 w-auto text-xs"
              >
                <option value="">{step.action === 'notify' ? 'Whoever owns it / sales managers' : 'Choose a person'}</option>
                {(users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}

        {(step.type === 'message' || step.type === 'question' || step.type === 'action') && (
          <div className="w-56">
            <TargetSelect value={step.next} empty="Then: next step" onPick={(next) => onChange({ ...step, next } as BotStep)} />
          </div>
        )}
      </div>
    </div>
  );
}
